import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Film } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/abd4f8f5-ed5f-494d-9745-ce8734069e02/images/e58e17d8f04e42f5ba5bf9446f3325f37dfd178211712442cae2db36d2963264.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Giriş başarılı");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      if (err.response?.status === 403) {
        toast.info("E-posta adresiniz doğrulanmamış. Kodu girin.");
        navigate("/verify", { state: { email } });
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute bottom-12 left-12 max-w-sm">
          <Film className="w-8 h-8 text-gold mb-4" />
          <h2 className="font-serif text-4xl leading-tight">Tekrar hoş geldin.</h2>
          <p className="text-white/60 mt-3">Projelerine kaldığın yerden devam et.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <p className="text-sm uppercase tracking-[0.3em] text-gold mb-3">Giriş</p>
          <h1 className="font-serif text-4xl mb-8">Hesabına gir</h1>
          <div className="space-y-5">
            <div>
              <Label className="text-white/70">E-posta</Label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="login-email-input"
                className="mt-2 bg-[#121212] border-white/10 focus:border-gold focus-visible:ring-gold h-11"
                placeholder="ornek@eposta.com"
              />
            </div>
            <div>
              <Label className="text-white/70">Şifre</Label>
              <Input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid="login-password-input"
                className="mt-2 bg-[#121212] border-white/10 focus:border-gold focus-visible:ring-gold h-11"
                placeholder="••••••••"
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full bg-gold text-black hover:bg-gold-light h-11 rounded-md">
              {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
            </Button>
          </div>
          <p className="text-sm text-white/50 mt-6">
            Hesabın yok mu?{" "}
            <Link to="/register" className="text-gold hover:underline" data-testid="go-register-link">Kayıt ol</Link>
          </p>
          <p className="text-xs text-white/30 mt-3">
            Stüdyo ekibi misiniz?{" "}
            <Link to="/admin/login" className="text-gold/70 hover:text-gold hover:underline" data-testid="go-admin-login-link">Yönetici girişi</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
